import type { SkinKind } from "@highmarch/domain";
import type { WornSet } from "../../render/skins/wornSkins";
import { SKIN_LOOKS } from "../../render/skins/looks";
import { closeBtnHtml } from "../kit";
import "../styles/skinSwitcher.css";

/** One kind's choice: absent = what the save wears, null = stock, a skin id = that skin. */
type Pick = Partial<Record<SkinKind, string | null>>;

const STORE = "highmarch.debug.skins";
const KINDS: readonly { kind: SkinKind; title: string }[] = [
  { kind: "tower", title: "Towers (all 8)" }, { kind: "fortress", title: "Fortress" }, { kind: "troop", title: "Troops" },
  { kind: "road", title: "Road" }, { kind: "emblem", title: "Banner" }, { kind: "frame", title: "Title frame" }, { kind: "kill", title: "Kill effect" },
  { kind: "pet", title: "Pet" },
];

/**
 * The skin switcher for testing (Settings → Skin switcher, everywhere for now — Kevin plays the Home Screen app, where
 * no URL flag reaches; plus a SKINS button with ?debug or ?skins): wear any skin at once, owned or not — on the hub
 * and in a level. It never touches the save: it is an override laid over what the save wears, kept in this browser
 * (localStorage) so it survives reloads, and "Back to my save" drops it. `?skin=bronze,moonstone` sets it from the URL
 * (any skin id or its last part). Towers re-dress where they stand; troops on their next spawn; a new fortress MODEL
 * (Dragonlord, Moonstone) shows at once on the hub and from the next level in a battle.
 */
export class SkinSwitcher {
  /** ?debug / ?skins: the floating SKINS button and `?skin=` (the Settings row is always there). */
  static readonly urlFlag = typeof location !== "undefined" && /[?&](debug|skins?)\b/.test(location.search);
  private static pick: Pick = SkinSwitcher.load();
  /** Set by features/cosmetics.ts: re-applies the worn set after a change. */
  static onChange: (() => void) | null = null;
  /** The skin ids by kind (shop.json `skins`), set by features/cosmetics.ts. */
  static catalogue: readonly { id: string; kind: SkinKind; pet?: string }[] = [];
  /** The tower ids a tower pick applies to (every non-fortress tower). */
  static towers: readonly string[] = [];

  /** What to wear: the save's set with the override on top. */
  static apply(base: WornSet): WornSet {
    const p = SkinSwitcher.pick, has = (k: SkinKind) => Object.prototype.hasOwnProperty.call(p, k);
    let towers = base.towers;
    if (has("tower")) { const m = new Map<string, string>(); if (p.tower) for (const t of SkinSwitcher.towers) m.set(t, p.tower); towers = m; }
    // a pet skin fits its own pet (docs/PETS.md): the pick goes on that pet, "Stock" takes every pet's off
    let pets = base.pets;
    if (has("pet")) { const m = new Map<string, string>(); const fit = SkinSwitcher.catalogue.find((c) => c.id === p.pet)?.pet; if (p.pet && fit) m.set(fit, p.pet); pets = m; }
    return {
      ...base,
      towers, pets,
      fortress: has("fortress") ? p.fortress ?? null : base.fortress,
      troop: has("troop") ? p.troop ?? null : base.troop,
      road: has("road") ? p.road ?? null : base.road,
      emblem: has("emblem") ? p.emblem ?? null : base.emblem,
      frame: has("frame") ? p.frame ?? null : base.frame ?? null,
      kill: has("kill") ? p.kill ?? null : base.kill ?? null,
    };
  }

  /** A short line for the Settings row: what the override wears, or that the save's skins are on. */
  static summary(): string {
    const p = SkinSwitcher.pick, parts = KINDS.filter(({ kind }) => Object.prototype.hasOwnProperty.call(p, kind))
      .map(({ kind }) => { const id = p[kind]; return id ? SKIN_LOOKS[id]?.name ?? id.split(".")[1] : `stock ${kind}`; });
    return parts.length ? `Testing: ${parts.join(", ")}` : "Try any skin, owned or not. Your save is untouched";
  }

  static active(): boolean { return Object.keys(SkinSwitcher.pick).length > 0; }

  static set(kind: SkinKind, id: string | null | undefined): void {
    if (id === undefined) delete SkinSwitcher.pick[kind]; else SkinSwitcher.pick[kind] = id;
    SkinSwitcher.save();
    SkinSwitcher.onChange?.();
  }

  static clear(): void { SkinSwitcher.pick = {}; SkinSwitcher.save(); SkinSwitcher.onChange?.(); }

  /** `?skin=bronze,moonstone` (after the catalogue is known): each name sets its kind. */
  static fromUrl(): void {
    if (!SkinSwitcher.urlFlag) return;
    const raw = new URLSearchParams(location.search).get("skin");
    if (!raw) return;
    for (const name of raw.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)) {
      const s = SkinSwitcher.catalogue.find((c) => c.id === name || c.id.split(".")[1] === name);
      if (s) SkinSwitcher.pick[s.kind] = s.id;
      else if (name.startsWith("stock")) for (const k of KINDS) SkinSwitcher.pick[k.kind] = null;
    }
    SkinSwitcher.save();
  }

  /** The switcher sheet. */
  static open(parent: HTMLElement): HTMLElement {
    document.querySelector(".sheet.skinswitch")?.remove();
    const sheet = document.createElement("div");
    sheet.className = "sheet panel skinswitch";
    const render = () => {
      const p = SkinSwitcher.pick;
      const chip = (kind: SkinKind, id: string | null | undefined, label: string) => {
        const on = id === undefined ? !Object.prototype.hasOwnProperty.call(p, kind) : p[kind] === id;
        return `<button class="ssw-chip${on ? " on" : ""}" data-kind="${kind}" data-id="${id === undefined ? "" : id ?? "stock"}">${label}</button>`;
      };
      sheet.innerHTML = `
        <div class="sheethead"><div class="titles"><b class="disp">Skin switcher</b><small>Testing · any skin, owned or not · the save is untouched</small></div>${closeBtnHtml()}</div>
        ${KINDS.map(({ kind, title }) => `<div class="ssw-row"><h4>${title}</h4><div class="ssw-chips">
          ${chip(kind, undefined, "My save")}${chip(kind, null, "Stock")}
          ${SkinSwitcher.catalogue.filter((s) => s.kind === kind).map((s) => chip(kind, s.id, SKIN_LOOKS[s.id]?.name ?? s.id.split(".")[1])).join("")}
        </div></div>`).join("")}
        <div class="ssw-foot"><button class="btn dark ssw-clear">Back to my save</button><small>Towers change where they stand · troops on their next spawn · a new fortress model from the next battle</small></div>`;
      sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
      sheet.querySelector<HTMLButtonElement>(".ssw-clear")!.onclick = () => { SkinSwitcher.clear(); render(); };
      sheet.querySelectorAll<HTMLButtonElement>(".ssw-chip").forEach((b) => {
        b.onclick = () => {
          const kind = b.dataset.kind as SkinKind, id = b.dataset.id ?? "";
          SkinSwitcher.set(kind, id === "" ? undefined : id === "stock" ? null : id);
          render();
          SkinSwitcher.refreshFab();
        };
      });
    };
    render();
    parent.append(sheet);
    return sheet;
  }

  /** A small SKINS button over every screen (hub and battle) while the switcher is on, so a battle can be re-skinned mid-wave. */
  static mountFab(parent: HTMLElement): void {
    if (!SkinSwitcher.urlFlag || document.querySelector(".ssw-fab")) return;
    const b = document.createElement("button");
    b.className = "ssw-fab";
    b.type = "button";
    b.onclick = (e) => { e.stopPropagation(); SkinSwitcher.open(parent); };
    parent.append(b);
    SkinSwitcher.refreshFab();
  }

  private static refreshFab(): void {
    const b = document.querySelector<HTMLButtonElement>(".ssw-fab");
    if (b) { b.textContent = SkinSwitcher.active() ? "SKINS •" : "SKINS"; b.classList.toggle("on", SkinSwitcher.active()); }
  }

  private static load(): Pick {
    try { const v = JSON.parse(localStorage.getItem(STORE) ?? "{}") as unknown; return v && typeof v === "object" ? v as Pick : {}; } catch { return {}; }
  }
  private static save(): void {
    try { if (SkinSwitcher.active()) localStorage.setItem(STORE, JSON.stringify(SkinSwitcher.pick)); else localStorage.removeItem(STORE); } catch { /* private mode */ }
  }
}
